/**
 * Core Wealthica reconnection mutation
 * 
 * Handles the actual API call to Wealthica's reconnect endpoint
 * Separated from persistence logic for clean separation of concerns
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/stores/authStore';
import { logger } from '@/services/LoggingService';
import { wealthica2Client } from '../../client';
import type { WealthicaConnection } from '../../types';

interface ReconnectParams {
  connectionData: WealthicaConnection;
}

interface ReconnectResult {
  success: boolean;
  data?: {
    accounts?: any[];
  };
  message?: string;
  status?: string;
}

export function useWealthicaReconnectMutation() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation<ReconnectResult, Error, ReconnectParams>({
    mutationFn: async ({ connectionData }) => {
      if (!user) {
        throw new Error('You must be logged in to refresh your account.');
      }

      if (!connectionData?.institutionId) {
        throw new Error('Cannot refresh: missing institution information.');
      }

      logger.info('useWealthicaReconnectMutation', 'Starting account refresh process', {
        institutionId: connectionData.institutionId,
        providerName: connectionData.providerName
      });

      const result = await wealthica2Client.reconnect({
        reference: user.id,
        institutionId: connectionData.institutionId
      });

      if (!result.success) {
        throw new Error(result.message || 'Failed to refresh account');
      }

      logger.info('useWealthicaReconnectMutation', 'Account refresh completed', {
        status: result.status,
        accountsCount: result.data?.accounts?.length || 0
      });

      return result;
    },
    onSuccess: (data) => {
      // Only invalidate and update what we have fresh data for
      queryClient.invalidateQueries({ queryKey: ['wealthica2', 'accounts'] });
      queryClient.invalidateQueries({ queryKey: ['wealthica2', 'positions'] });
      
      // For multi-store support, also invalidate multi-store queries
      queryClient.invalidateQueries({ queryKey: ['multiWealthica2', 'accounts'] });
      queryClient.invalidateQueries({ queryKey: ['multiWealthica2', 'positions'] });
      
      // ✅ FIXED: Don't invalidate positions cache - reconnect API doesn't return position data
      // Preserve cached positions so user doesn't lose their data view
      
      if (data.data?.accounts) {
        queryClient.setQueryData(['wealthica2', 'accounts'], data.data.accounts);
        
        logger.info('useWealthicaReconnectMutation', 'Updated account cache, preserved position cache', {
          accountsCount: data.data.accounts?.length || 0,
          note: 'Position cache preserved for continued data access, multi-store queries invalidated'
        });
      }
    },
    onError: (error) => {
      logger.error('useWealthicaReconnectMutation', 'Account refresh failed', { error });
    }
  });
}